'use client';

import Image from 'next/image';
import { JSX } from 'react';

import { Text } from '@videoblocks/design-system';
import { type BillingInfo, PaymentMethodType } from '@videoblocks/subscription-service-client';

interface CreditCardDisplayProps {
    billingInfo: BillingInfo;
}

const formatExpiration = (expMonth: number, expYear: number): string => {
    const month = expMonth.toString().padStart(2, '0');
    const year = expYear.toString().slice(-2);
    return `${month}/${year}`;
};

export const CreditCardDisplay = ({ billingInfo }: CreditCardDisplayProps): JSX.Element => {
    const {
        cardType = '',
        lastFour = '',
        firstName = '',
        lastName = '',
        expMonth,
        expYear,
        paymentMethodType,
    } = billingInfo;

    if (paymentMethodType === PaymentMethodType.Paypal) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                    <Image
                        src="/assets/images/paypal-logo.png"
                        alt="PayPal"
                        width={28}
                        height={28}
                        className="object-contain -mt-1"
                    />
                    <div className="flex-1">
                        <Text fontSize="base" fontWeight="semibold" className="text-gray-900">
                            PayPal
                        </Text>
                        <Text fontSize="sm" className="text-gray-600">
                            {firstName} {lastName}
                        </Text>
                    </div>
                </div>
            </div>
        );
    }

    if (paymentMethodType === PaymentMethodType.GooglePay) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                    <Image
                        src="/assets/images/google-pay-mark_800.svg"
                        alt="Google Pay"
                        width={28}
                        height={28}
                        className="object-contain"
                    />
                    <div className="flex-1">
                        <Text fontSize="base" fontWeight="semibold" className="text-gray-900">
                            Google Pay
                        </Text>
                        <Text fontSize="sm" className="text-gray-600">
                            {cardType} ••••{lastFour} • {firstName} {lastName}
                        </Text>
                    </div>
                </div>
            </div>
        );
    }

    if (paymentMethodType === PaymentMethodType.ApplePay) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                    <Image
                        src="/assets/images/apple-pay-mark-rgb-041619.svg"
                        alt="Apple Pay"
                        width={28}
                        height={28}
                        className="object-contain"
                    />
                    <div className="flex-1">
                        <Text fontSize="base" fontWeight="semibold" className="text-gray-900">
                            Apple Pay
                        </Text>
                        <Text fontSize="sm" className="text-gray-600">
                            {cardType} ••••{lastFour} • {firstName} {lastName}
                        </Text>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
                <div className="flex-1">
                    <Text fontSize="base" fontWeight="semibold" className="text-gray-900">
                        {cardType} ••••{lastFour}
                    </Text>
                    <Text fontSize="sm" className="text-gray-600">
                        {firstName} {lastName} • {expMonth && expYear ? formatExpiration(expMonth, expYear) : ''}
                    </Text>
                </div>
            </div>
        </div>
    );
};
